import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import fakerbr from 'faker-br';

import { MaskPipe } from 'ngx-mask';
import DDIOptions from 'src/app/data/ddi';
import Utils, { GetHelper, payloadsEqual, UtilsValidators } from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
  BasicInfo,
  SignupService,
} from 'src/app/services/signup/signup.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { ToastService } from 'src/app/services/toast/toast.service';

@Component({
  selector: 'app-signup-unified-person-complementary-general',
  templateUrl: './signup-unified-person-complementary-general.component.html',
  styleUrls: ['./signup-unified-person-complementary-general.component.scss'],
})
export class SignupUnifiedPersonComplementaryGeneralComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() userRole: string;

  DDIOptions = DDIOptions;

  form = this.formBuilder.group({
    document: [null, [Validators.required, UtilsValidators.cpf]],
    full_name: [null, [Validators.required, UtilsValidators.fullName]],
    email: [null, [Validators.required, UtilsValidators.email]],
    interest_fund: [null, []],
    agent: [null, []],

    birth_date: [
      null,
      [Validators.required, Utils.validateMinDate, Utils.validateLegalAge],
    ],
    marital_status: [null, [Validators.required]],
    joint_obligation: [false, []],
    gender: [null, [Validators.required]],
    revenues: [null, [Validators.required]],
    mother_name: [null, [Validators.required, UtilsValidators.fullName]],
    father_name: [null, [Utils.validateWhenNotEmpty(UtilsValidators.fullName)]],
    naturalness: [null, [Validators.required]],
    naturalness_city: [null, [Validators.required]],

    spouse_document: [null, []],
    spouse_name: [null, []],
    marriage_bond: [null, []],

    nationality: ['23', [Validators.required]],

    type: [null, [Validators.required]],
    rg: [null, [Validators.required]],
    dispatching_agency: [null, [Validators.required]],
    uf: [null, [Validators.required]],
    expedition_date: [
      null,
      [Validators.required, Utils.validateMaxMinToday, Utils.validateMinDate],
    ],

    cellphone_ddi: ['55', []],
    cellphone_number: [
      null,
      [
        Validators.required,
        Utils.validateWhenNotEmpty(UtilsValidators.celularWithDDI),
      ],
    ],
    telephone_ddi: ['55', []],
    telephone_number: [
      null,
      [Utils.validateWhenNotEmpty(UtilsValidators.telefone)],
    ],

    site: [null, []],
    authorization_bacen_queries: [false, []],
    /** Cedente: integrante do SFN (Sistema Financeiro Nacional) */
    sfn: [false, []],

    relationship_with_other_institutions: [false, []],
    institution_relationship: [null, []],
    pep_relationship: [false, []],
    pepRelationshipDocument: [null, []],
    pepRelationshipName: [null, []], // Mantido para compatibilidade
    pepRelationshipFullName: [null, []], // Novo campo para exibição sem interferência
    pepBond: [null, []],
  });

  today = Utils.todayString();

  isForeign = false;

  items = [
    { value: '1', label: 'Sim' },
    { value: '2', label: 'Não' },
  ];

  haveBasicInfo: boolean = false;

  invitedInfo: BasicInfo = {
    document: null,
    name: null,
    email: null,
  };

  data = null;

  /** Snapshot do payload após o GET; usado para evitar PATCH/PUT quando nada mudou. */
  private initialPayload: Record<string, any> | null = null;

  event: any;
  foundPerson = false;
  foundSpouse = false;
  foundRelationPerson = false;

  fundOptions: SelectItem[] = [];

  loadingContent = false;
  agentOptions: SelectItem[] = [];
  legacyVersionChecked = false;

  cities_options: SelectItem[] = [];

  documentTypes: SelectItem[] = [];

  // Controle de APIs que precisam ser carregadas antes de esconder o loading
  private loadingAPIs = {
    dataAndFillForm: false,
    funds: false,
    agents: false,
    cities: false, // Opcional - só se houver naturalness
  };

  // Cache para evitar chamadas duplicadas de getCities
  private citiesLoadingState: number | null = null;

  /**
   * Verifica se todas as APIs necessárias foram carregadas
   * e desabilita o loading se todas terminarem
   */
  private checkAndDisableLoading(): void {
    const hasNaturalness =
      this.form.controls.naturalness?.value != null &&
      this.form.controls.naturalness?.value !== '';

    // Se tem naturalness, precisa esperar getCities também
    const needsCities = hasNaturalness;

    const allLoaded =
      this.loadingAPIs.dataAndFillForm &&
      this.loadingAPIs.funds &&
      this.loadingAPIs.agents &&
      (!needsCities || this.loadingAPIs.cities);

    if (allLoaded) {
      this.loadingContent = false;
    }
  }

  /**
   * Atualiza a lista de tipos de documento filtrados e formatados com abreviações
   */
  private updateDocumentTypes(): void {
    const allTypes = this.signupService.choices().document_types || [];

    // Mapeamento de abreviações para tipos de documento conhecidos
    // Ordenado por tamanho (maior primeiro) para priorizar correspondências mais específicas
    const abbreviations: Array<{ key: string; abbr: string }> = [
      {
        key: 'Carteira Funcional da Adm Pública, Justiça e Forças Armadas',
        abbr: 'CFAP',
      },
      { key: 'Cédula de Identidade de Estrangeiro', abbr: 'CIE' },
      { key: 'Registro Nacional de Estrangeiro', abbr: 'RNE' },
      { key: 'Carteira de Trabalho Previdência Social', abbr: 'CTPS' },
      { key: 'Carteira Nacional de Habilitação', abbr: 'CNH' },
      { key: 'Carteira de Identidade', abbr: 'RG' },
    ];

    // Documentos que não devem ter abreviação
    const noAbbreviation = [
      'Passaporte',
      'Carteira de Identidade Profissional',
    ];

    this.documentTypes = allTypes
      .filter((item: SelectItem) => {
        // Remove "Cadastro Pessoa Física"
        // Remove "Carteira de Identidade Profissional" EXCETO se contiver "expedida por Conselho de Classe"
        const label = item.label?.toLowerCase() || '';
        const isProfessionalWithCouncil =
          label.includes('carteira de identidade profissional') &&
          label.includes('expedida por conselho de classe');
        return (
          !label.includes('cadastro pessoa fisica') &&
          !label.includes('cadastro pessoa física') &&
          !(
            label.includes('carteira de identidade profissional') &&
            !isProfessionalWithCouncil
          )
        );
      })
      .map((item: SelectItem) => {
        let label = item.label || '';

        // Remove especificamente o prefixo "RG - " da "Carteira de Identidade Profissional"
        // Verifica se contém "Carteira de Identidade Profissional" e "expedida por Conselho de Classe"
        // Normaliza espaços múltiplos para garantir correspondência
        const labelLowerBefore = label
          .toLowerCase()
          .trim()
          .replace(/\s+/g, ' ');

        const hasProfessional = labelLowerBefore.includes(
          'carteira de identidade profissional'
        );
        const hasCouncil = labelLowerBefore.includes(
          'expedida por conselho de classe'
        );
        const isProfessionalIdentity = hasProfessional && hasCouncil;

        if (isProfessionalIdentity) {
          // Define diretamente o formato desejado: "Carteira de Identidade Profissional (expedida pelo conselho classe)"
          label =
            'Carteira de Identidade Profissional (expedida pelo conselho classe)';
        }

        const labelLower = label.toLowerCase().trim().replace(/\s+/g, ' ');

        // Verifica se o documento não deve ter abreviação
        // Normaliza espaços para garantir correspondência correta
        // NOTA: Carteira de Identidade Profissional terá abreviação "CIP", então não pula
        const shouldSkipAbbreviation = noAbbreviation.some((noAbbr) => {
          const normalizedNoAbbr = noAbbr.toLowerCase().replace(/\s+/g, ' ');
          return labelLower.includes(normalizedNoAbbr);
        });

        if (shouldSkipAbbreviation) {
          return { ...item, label };
        }

        // Se for Carteira de Identidade Profissional, adiciona "CIP" e retorna direto
        if (isProfessionalIdentity) {
          const finalLabel = `CIP - ${label}`;
          return { ...item, label: finalLabel };
        }

        // Verifica se o label já começa com uma abreviação conhecida (ex: "CNH - ...")
        // Se sim, remove a abreviação duplicada do início
        const knownAbbrevs = abbreviations.map((e) => e.abbr);
        for (const abbr of knownAbbrevs) {
          const prefix = `${abbr} - `;
          if (label.startsWith(prefix)) {
            label = label.substring(prefix.length);
            break;
          }
        }

        const cleanLabelLower = label.toLowerCase().trim();

        // Procura por abreviação no mapeamento (prioriza correspondências mais específicas)
        let abbreviation = '';

        // Primeiro, tenta encontrar correspondência exata
        for (const entry of abbreviations) {
          const normalizedKey = entry.key
            .toLowerCase()
            .trim()
            .replace(/\s+/g, ' ');
          const normalizedCleanLabel = cleanLabelLower.replace(/\s+/g, ' ');
          if (normalizedCleanLabel === normalizedKey) {
            abbreviation = entry.abbr;
            break;
          }
        }

        // Se não encontrou exato, procura a correspondência mais longa e específica
        // Como o array está ordenado do maior para o menor, a primeira correspondência
        // encontrada será a mais específica
        if (!abbreviation) {
          // Primeiro, verifica todas as chaves longas (>30 caracteres)
          for (const entry of abbreviations) {
            if (entry.key.length > 30) {
              const keyLower = entry.key
                .toLowerCase()
                .trim()
                .replace(/\s+/g, ' ');
              const normalizedCleanLabel = cleanLabelLower.replace(/\s+/g, ' ');
              if (normalizedCleanLabel.includes(keyLower)) {
                abbreviation = entry.abbr;
                break; // Para imediatamente ao encontrar correspondência longa
              }
            }
          }

          // Se não encontrou chave longa, verifica chaves curtas
          // Mas só aceita se não for parte de uma chave maior já verificada
          if (!abbreviation) {
            for (const entry of abbreviations) {
              if (entry.key.length <= 30) {
                const keyLower = entry.key.toLowerCase().trim();

                // Verifica se esta chave curta não está contida em nenhuma chave maior
                let isPartOfLongerKey = false;
                const normalizedKeyLower = keyLower.replace(/\s+/g, ' ');
                const normalizedCleanLabel = cleanLabelLower.replace(
                  /\s+/g,
                  ' '
                );
                for (const longEntry of abbreviations) {
                  if (longEntry.key.length > 30) {
                    const longKeyLower = longEntry.key
                      .toLowerCase()
                      .trim()
                      .replace(/\s+/g, ' ');
                    if (
                      longKeyLower.includes(normalizedKeyLower) &&
                      longKeyLower !== normalizedKeyLower
                    ) {
                      // Esta chave curta está contida em uma chave maior
                      // Verifica se a chave maior também está no label
                      if (normalizedCleanLabel.includes(longKeyLower)) {
                        isPartOfLongerKey = true;
                        break;
                      }
                    }
                  }
                }

                // Só aceita a chave curta se não for parte de uma chave maior
                if (!isPartOfLongerKey) {
                  // Verifica se é "Carteira de Identidade" mas NÃO é "Carteira de Identidade Profissional"
                  const normalizedCleanLabel = cleanLabelLower.replace(
                    /\s+/g,
                    ' '
                  );
                  const normalizedKeyLower = keyLower.replace(/\s+/g, ' ');
                  const isProfessionalIdentityDoc =
                    normalizedCleanLabel.includes(
                      'carteira de identidade profissional'
                    ) &&
                    normalizedCleanLabel.includes(
                      'expedida por conselho de classe'
                    );

                  const matchesShortKey =
                    normalizedCleanLabel === normalizedKeyLower ||
                    normalizedCleanLabel.startsWith(normalizedKeyLower + ' ');

                  if (matchesShortKey && !isProfessionalIdentityDoc) {
                    abbreviation = entry.abbr;
                    break;
                  }
                }
              }
            }
          }
        }

        // Verifica novamente se é "Carteira de Identidade Profissional"
        // Usa cleanLabelLower para verificar após todos os processamentos
        // Normaliza espaços para garantir correspondência
        const cleanLabelLowerNormalized = cleanLabelLower.replace(/\s+/g, ' ');
        const isStillProfessionalIdentity =
          cleanLabelLowerNormalized.includes(
            'carteira de identidade profissional'
          ) &&
          (cleanLabelLowerNormalized.includes(
            'expedida por conselho de classe'
          ) ||
            cleanLabelLowerNormalized.includes(
              'expedida pelo conselho classe'
            ));

        if (isStillProfessionalIdentity && !isProfessionalIdentity) {
          // Se ainda é Professional Identity mas não foi detectado no início, adiciona CIP
          abbreviation = 'CIP';
        } else if (isProfessionalIdentity || isStillProfessionalIdentity) {
          // Já foi processado anteriormente e retornado com CIP, então não adiciona outra abreviação
          abbreviation = '';
        } else {
          // Se não encontrou abreviação específica, cria uma baseada nas palavras
          if (!abbreviation && label.length > 0) {
            const words = label.split(' ').filter((w) => w.length > 2);
            if (words.length > 0) {
              // Pega as primeiras letras maiúsculas de até 3 palavras importantes
              abbreviation = words
                .slice(0, 3)
                .map((w) => w[0].toUpperCase())
                .join('');
            } else {
              // Se não há palavras suficientes, pega as primeiras letras do label
              abbreviation = label
                .split('')
                .filter((c) => /[A-Za-z]/.test(c))
                .slice(0, 3)
                .join('')
                .toUpperCase();
            }
          }
        }

        // Adiciona a abreviação antes do label, mantendo o value original
        const finalLabel = abbreviation ? `${abbreviation} - ${label}` : label;

        return {
          ...item,
          label: finalLabel,
        };
      });
  }

  constructor(
    private formBuilder: FormBuilder,
    public signupService: SignupService,
    private maskPipe: MaskPipe,
    private authService: AuthService,
    private api: ApiService,
    private toast: ToastService,
    private storageService: StorageService,
    private route: ActivatedRoute
  ) {}

  ngOnDestroy(): void {
    if (this.event) {
      this.event.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando o userRole é definido ou muda, atualiza a validação de authorization_bacen_queries
    if (changes['userRole']) {
      this.setAuthorizationBacenQueriesValidation();
      // Se o estado civil já está como casado/unido, atualiza a validação do marriage_bond
      const maritalStatus = this.form.controls['marital_status']?.value;
      if (maritalStatus === '2' || maritalStatus === '3') {
        this.setMarried();
      }
    }
  }

  /**
   * Define a validação do campo authorization_bacen_queries baseado no userRole
   */
  setAuthorizationBacenQueriesValidation(): void {
    if (this.userRole !== 'provider' && this.userRole !== 'shareholder') {
      this.form.controls.authorization_bacen_queries.setValidators([
        Validators.requiredTrue,
      ]);
    } else {
      this.form.controls.authorization_bacen_queries.setValidators([]);
    }
    this.form.controls.authorization_bacen_queries.updateValueAndValidity();
  }

  /**
   * Valida apenas os campos obrigatórios (com asterisco) para shareholder
   * @returns true se todos os campos obrigatórios estão válidos
   */
  isShareholderFormValid(): boolean {
    // Campos obrigatórios base
    const requiredFields = [
      'document',
      'full_name',
      'birth_date',
      'gender',
      'marital_status',
      'naturalness',
      'naturalness_city',
      'nationality',
      'cellphone_number',
      'email',
      'mother_name',
      'type',
      'rg',
      'dispatching_agency',
      'uf',
      'expedition_date',
    ];

    const invalidFields: string[] = [];

    // Verifica campos obrigatórios base
    for (const field of requiredFields) {
      const control = this.form.get(field);
      if (!control || !control.valid) {
        invalidFields.push(field);
      }
    }

    // Verifica campos condicionais do estado civil (casado/unido)
    const maritalStatus = this.form.get('marital_status')?.value;
    if (maritalStatus === '2' || maritalStatus === '3') {
      // Para cotista PF, marriage_bond não é obrigatório
      const spouseFields =
        this.userRole === 'shareholder'
          ? ['spouse_document', 'spouse_name']
          : ['spouse_document', 'spouse_name', 'marriage_bond'];
      for (const field of spouseFields) {
        const control = this.form.get(field);
        if (!control || !control.valid) {
          invalidFields.push(field);
        }
      }
    }

    // Verifica campos condicionais do PEP (só se o checkbox estiver marcado)
    const pepRelationship = this.form.get('pep_relationship')?.value;
    if (pepRelationship) {
      const pepFields = [
        'pepRelationshipDocument',
        'pepRelationshipFullName', // Usar o novo campo para validação
        'pepBond',
      ];
      for (const field of pepFields) {
        const control = this.form.get(field);
        if (!control || !control.valid) {
          invalidFields.push(field);
        }
      }
    }

    // Verifica campos opcionais que, se preenchidos, devem estar válidos
    const optionalFields = ['father_name'];
    for (const field of optionalFields) {
      const control = this.form.get(field);
      if (
        control &&
        control.value &&
        control.value.trim() !== '' &&
        !control.valid
      ) {
        invalidFields.push(field);
      }
    }

    if (invalidFields.length > 0) {
      return false;
    }

    return true;
  }

  ngOnInit(): void {
    try {
      // Iniciar loading spinner
      this.loadingContent = true;

      this.form.valueChanges.subscribe((status) => {
        Utils.getErrors(this.form);
      });

      // Atualiza os tipos de documento quando os choices são carregados
      this.updateDocumentTypes();
      this.signupService.choicesFetchedEvent.subscribe(() => {
        this.updateDocumentTypes();
      });

      // Chamar todas as APIs em paralelo para carregar mais rápido
      Promise.all([
        this.getDataAndFillForm(),
        this.getFunds(),
        this.getAgents(),
        // Não precisa mais chamar checkAndCreateLegacyVersion - o backend já cria versão 1 automaticamente
      ])
        .then(() => {
          // Após todas as APIs terminarem (incluindo legacy version), garantir que loading está desativado
          this.loadingContent = false;
        })
        .catch((error) => {
          console.error('[PEP] Erro no Promise.all:', error);
          // Mesmo com erro, desativar loading após legacy version terminar
          if (this.legacyVersionChecked) {
            this.loadingContent = false;
          }
        });
    } catch (error) {
      console.error('[PEP] Erro no início do ngOnInit:', error);
    }

    try {
      if (this.userRole === 'provider') {
        this.form.controls.agent.setValidators([Validators.required]);
      }

      if (this.userRole === 'assignor') {
        this.form.controls.revenues.setValidators([]);
        this.form.controls.revenues.updateValueAndValidity();
      }

      if (this.userRole === 'shareholder') {
        // Remove obrigatoriedade dos campos que não devem aparecer para shareholder
        this.form.controls.site.setValidators([]);
        this.form.controls.revenues.setValidators([]);
        this.form.controls.authorization_bacen_queries.setValidators([]);

        this.form.controls.site.updateValueAndValidity();
        this.form.controls.revenues.updateValueAndValidity();
        this.form.controls.authorization_bacen_queries.updateValueAndValidity();
      }
    } catch (error) {
      console.error('[PEP] Erro ao configurar validadores por role:', error);
    }
    this.form.controls.pepRelationshipDocument.valueChanges
      .pipe(
        debounceTime(500), // Aguardar 500ms após parar de digitar
        distinctUntilChanged() // Só disparar se o valor mudou
      )
      .subscribe(async (value) => {
        if (!value) {
          this.foundRelationPerson = false;
          // Força a validação para manter os erros de validação
          this.form.controls.pepRelationshipDocument.updateValueAndValidity();
          return;
        }

        const document = Utils.onlyNumbers(value);

        if (document.length === 11) {
          // Verifica se é o mesmo documento do usuário
          const sameDocument =
            Utils.onlyNumbers(this.form.controls.document.value) === document;

          // Força a validação primeiro para obter os erros de validação (CPF, required, etc)
          this.form.controls.pepRelationshipDocument.updateValueAndValidity();

          const currentErrors =
            this.form.controls.pepRelationshipDocument.errors || {};

          if (sameDocument) {
            // Adiciona o erro de sameDocument mantendo os outros erros
            this.form.controls.pepRelationshipDocument.setErrors({
              ...currentErrors,
              sameDocument: true,
            });
          } else {
            // Remove apenas o erro de sameDocument, mantendo outros erros de validação
            delete currentErrors['sameDocument'];

            if (Object.keys(currentErrors).length === 0) {
              this.form.controls.pepRelationshipDocument.setErrors(null);
            } else {
              this.form.controls.pepRelationshipDocument.setErrors(
                currentErrors
              );
            }
          }
        } else {
          // Força a validação mesmo quando não tem 11 dígitos
          this.form.controls.pepRelationshipDocument.updateValueAndValidity();
        }
      });

    this.form.controls.spouse_document.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(async (value) => {
        if (!value) {
          this.foundSpouse = false;
        }

        if (value && value.length === 14) {
          const { name, foundPerson } = await this.api.getPersonName(value);

          this.foundSpouse = foundPerson;

          if (name) this.form.controls.spouse_name.setValue(name);
        }
      });
    this.form.controls.document.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(async (value) => {
        if (!value) {
          this.foundPerson = false;
        }

        const document = Utils.onlyNumbers(value);
        if (document.length === 11) {
          const { name, foundPerson } = await this.api.getPersonName(value);
          this.foundPerson = foundPerson;

          if (name) this.form.controls.full_name.setValue(name);
        }
      });
    // Remove números do campo de nome completo em tempo real
    this.form.controls.full_name.valueChanges.subscribe((value) => {
      if (value && typeof value === 'string') {
        const valueWithoutNumbers = value.replace(/\d/g, '');
        if (value !== valueWithoutNumbers) {
          this.form.controls.full_name.setValue(valueWithoutNumbers, {
            emitEvent: false,
          });
        }
      }
    });
    // Remove números do campo de nome completo da pessoa politicamente exposta em tempo real
    this.form.controls.pepRelationshipFullName.valueChanges.subscribe(
      (value) => {
        if (value && typeof value === 'string') {
          const valueWithoutNumbers = value.replace(/\d/g, '');
          if (value !== valueWithoutNumbers) {
            this.form.controls.pepRelationshipFullName.setValue(
              valueWithoutNumbers,
              {
                emitEvent: false,
              }
            );
          }
          // Sincronizar com o campo antigo para o payload
          this.form.controls.pepRelationshipName.setValue(valueWithoutNumbers, {
            emitEvent: false,
          });
        }
      }
    );
    this.form.controls.marital_status.valueChanges.subscribe(() => {
      this.setMarried();
    });
    this.form.controls.marital_status.valueChanges.subscribe(() => {
      this.setMarried();
    });

    this.form.controls.naturalness.valueChanges.subscribe((naturalness) => {
      // Resetar a flag quando o estado mudar (para permitir nova chamada)
      if (
        this.citiesLoadingState !== null &&
        this.citiesLoadingState !== naturalness
      ) {
        this.citiesLoadingState = null;
      }

      if (naturalness !== null && naturalness !== '') {
        this.getCities(naturalness);
      }

      this.form.controls.naturalness_city.reset();
      this.form.controls.naturalness_city.updateValueAndValidity();
    });
    this.form.controls.nationality.valueChanges.subscribe(() => {
      if (this.form.controls.nationality.value !== '23') {
        this.form.controls.naturalness.setValue(null);
        this.form.controls.naturalness_city.setValue(null);
        this.form.controls.naturalness.setValidators([]);
        this.form.controls.naturalness_city.setValidators([]);

        this.isForeign = true;
      } else {
        this.form.controls.naturalness.setValidators([Validators.required]);
        this.form.controls.naturalness_city.setValidators([
          Validators.required,
        ]);

        this.isForeign = false;
      }

      this.form.controls.naturalness.updateValueAndValidity();
      this.form.controls.naturalness_city.updateValueAndValidity();
    });
    this.event = this.signupService.sendPersonData.subscribe((value) => {
      if (value === 'complementaryGeneral') {
        this.sendData();
      }
    });
    if (this.userRole !== 'provider' && this.userRole !== 'shareholder') {
      this.form.controls.relationship_with_other_institutions.valueChanges.subscribe(
        (values) => {
          if (values) {
            this.form.controls.institution_relationship.setValidators([
              Validators.required,
            ]);
          } else {
            this.form.controls.institution_relationship.setValidators([]);
          }
          this.form.controls.institution_relationship.updateValueAndValidity();
        }
      );
    }
    if (this.signupService.basicInfo !== null) {
      this.haveBasicInfo = true;

      this.invitedInfo = this.signupService.basicInfo;

      this.form.patchValue({
        document: this.invitedInfo.document ?? null,
        full_name: this.invitedInfo.name ?? null,
        email: this.invitedInfo.email,
      });
    }
    const newUserDataStorage = this.storageService.getNewUserData();

    if (newUserDataStorage) {
      this.invitedInfo.document = newUserDataStorage.document;
      this.form.patchValue({
        document: newUserDataStorage.document,
      });
    }
    try {
      this.form.valueChanges.subscribe(() => {
        try {
          // Força a validação de todos os controles antes de verificar erros
          Object.keys(this.form.controls).forEach((key) => {
            this.form.controls[key].updateValueAndValidity({
              emitEvent: false,
            });
          });

          const errors = Utils.getErrors(this.form);

          // Usa setTimeout para garantir que todas as validações sejam concluídas antes de atualizar o status
          setTimeout(() => {
            const step = this.signupService.getPersonStep();
            const currentStatus = this.signupService.getPersonFormStatus(
              'complementaryGeneral'
            );
            const formValid = this.form.valid;

            const invalidControls = Object.keys(this.form.controls)
              .filter((key) => {
                const control = this.form.get(key);
                return control && !control.valid;
              })
              .map((key) => {
                const control = this.form.get(key);
                return {
                  key,
                  value: control?.value,
                  errors: control?.errors,
                  valid: control?.valid,
                };
              });

            // Para shareholder, valida apenas os campos obrigatórios (com asterisco)
            const isValid =
              this.userRole === 'shareholder'
                ? this.isShareholderFormValid()
                : this.form.valid;

            this.signupService.setPersonFormStatus(
              'complementaryGeneral',
              isValid
            );
          }, 0);
        } catch (error) {
          console.error('[PEP] Erro no callback do form.valueChanges:', error);
        }
      });
    } catch (error) {
      console.error(
        '[PEP] Erro ao configurar segundo subscribe form.valueChanges:',
        error
      );
    }

    // Força uma atualização inicial do status após o subscribe ser configurado
    setTimeout(() => {
      // Para shareholder, valida apenas os campos obrigatórios (com asterisco)
      const isValid =
        this.userRole === 'shareholder'
          ? this.isShareholderFormValid()
          : this.form.valid;

      this.signupService.setPersonFormStatus('complementaryGeneral', isValid);
    }, 100);
    this.form.controls.birth_date.valueChanges.subscribe((value) => {
      this.form.controls.expedition_date.setValidators([
        Validators.required,
        Utils.validateMaxMinToday,
        Utils.validateMinDate,
        (control: FormControl) => {
          if (!control.value || !value) return null;

          // Verifica se a data de nascimento é válida antes de comparar
          const birthDateErrors = this.form.controls.birth_date.errors;
          if (
            birthDateErrors &&
            (birthDateErrors['date'] ||
              birthDateErrors['min'] ||
              birthDateErrors['age'])
          ) {
            // Se a data de nascimento tem erros, não valida a data de expedição em relação a ela
            return null;
          }

          const birthDateArr = value.split('/');

          if (birthDateArr.length !== 3) return null;

          const birthDateDay = parseInt(birthDateArr[0]);
          const birthDateMonth = parseInt(birthDateArr[1]);
          const birthDateYear = parseInt(birthDateArr[2]);

          // Verifica se os valores são válidos
          if (
            isNaN(birthDateDay) ||
            isNaN(birthDateMonth) ||
            isNaN(birthDateYear)
          ) {
            return null;
          }

          const birthDate = new Date(
            birthDateYear,
            birthDateMonth - 1,
            birthDateDay
          );

          // Verifica se a data criada é válida
          if (isNaN(birthDate.getTime())) {
            return null;
          }

          const expedition_dateArr = control.value.split('/');

          if (expedition_dateArr.length !== 3) return null;

          const shippingDateDay = parseInt(expedition_dateArr[0]);
          const shippingDateMonth = parseInt(expedition_dateArr[1]);
          const shippingDateYear = parseInt(expedition_dateArr[2]);

          // Verifica se os valores são válidos
          if (
            isNaN(shippingDateDay) ||
            isNaN(shippingDateMonth) ||
            isNaN(shippingDateYear)
          ) {
            return null;
          }

          const shippingDate = new Date(
            shippingDateYear,
            shippingDateMonth - 1,
            shippingDateDay
          );

          // Verifica se a data criada é válida
          if (isNaN(shippingDate.getTime())) {
            return null;
          }

          if (shippingDate < birthDate) {
            return { minDate: true };
          }

          return null;
        },
      ]);

      this.form.controls.expedition_date.updateValueAndValidity();
    });
    try {
      if (!this.signupService.fillFormEvent) {
        console.error('[PEP] ERRO: signupService.fillFormEvent não existe!');
        throw new Error('fillFormEvent não existe');
      }
      this.signupService.fillFormEvent.subscribe(() => {
        const pepRelationship = fakerbr.random.boolean();

        const haveRelation = fakerbr.random.boolean();

        const birthdate_date = new Date();
        birthdate_date.setFullYear(birthdate_date.getFullYear() - 25);

        this.form.patchValue({
          full_name: this.invitedInfo.name
            ? this.form.controls.full_name.value
            : fakerbr.name.findName(undefined, undefined, 'male'),
          document: this.invitedInfo.document
            ? this.form.controls.document.value
            : this.maskPipe.transform(fakerbr.br.cpf(), '000.000.000-00'),
          birth_date: birthdate_date.toLocaleDateString('pt-BR', {
            timeZone: 'UTC',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
          email: this.invitedInfo.email
            ? this.form.controls.email.value
            : fakerbr.internet.email(),
          marital_status: '2',
          joint_obligation: false,
          gender: '1',
          spouse_document: this.maskPipe.transform(
            fakerbr.br.cpf(),
            '000.000.000-00'
          ),
          spouse_name: fakerbr.name.findName(undefined, undefined, 'female'),
          marriage_bond: '1',
          revenues: fakerbr.random.number({ min: 500000, max: 2000000 }),
          nationality: '23',
          naturalness: 1, // SP
          naturalness_city: 4707, // São Paulo
          type: '1',
          mother_name: fakerbr.name.findName(undefined, undefined, 'female'),
          father_name: fakerbr.name.findName(undefined, undefined, 'male'),
          rg: fakerbr.br.rg(),

          dispatching_agency: 'SSP - Secretaria de Segurança Pública',
          uf: 'SP',
          expedition_date: fakerbr.date.past(8).toLocaleDateString('pt-BR', {
            timeZone: 'UTC',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),

          cellphone_ddi: '55',
          cellphone_number: this.maskPipe.transform(
            '139' +
              String(fakerbr.random.number({ min: 11111111, max: 99999999 })),
            '(00) 00000-0000'
          ),
          telephone_ddi: '55',
          telephone_number: this.maskPipe.transform(
            '139' +
              String(fakerbr.random.number({ min: 1111111, max: 9999999 })),
            '(00) 0000-0000'
          ),

          site: fakerbr.internet.url(),

          authorization_bacen_queries: true,
        });

        if (this.userRole !== 'provider') {
          const fakePepName = pepRelationship ? fakerbr.name.findName() : null;
          this.form.patchValue({
            pep_relationship: pepRelationship,
            pepRelationshipDocument: pepRelationship
              ? this.maskPipe.transform(fakerbr.br.cpf(), '000.000.000-00')
              : null,
            pepRelationshipFullName: fakePepName, // Usar o novo campo
            pepRelationshipName: fakePepName, // Manter para compatibilidade
            pepBond: pepRelationship ? 'Sócio' : null,
            relationship_with_other_institutions: haveRelation,
            institution_relationship: haveRelation
              ? fakerbr.company.companyName()
              : '',
          });
        }

        // Carregar cidades de SP após preencher o formulário
        this.getCities(1);
      });
    } catch (error) {
      console.error('[PEP] Erro ao configurar subscribe fillFormEvent:', error);
    }

    // A validação de authorization_bacen_queries será aplicada no ngOnChanges
    // para garantir que o userRole esteja definido
    try {
      this.setAuthorizationBacenQueriesValidation();
    } catch (error) {
      console.error(
        '[PEP] Erro em setAuthorizationBacenQueriesValidation:',
        error
      );
    }

    // Subscribe para PEP relationship - disponível para todos os roles
    try {
      const pepControl = this.form.controls.pep_relationship;
      if (!pepControl) {
        console.error('[PEP] ERRO: pep_relationship control não existe!');
        console.error(
          '[PEP] Controles disponíveis:',
          Object.keys(this.form.controls)
        );
        throw new Error('pep_relationship control não existe');
      }

      pepControl.valueChanges.subscribe((value) => {
        try {
          this.verifyPepRelationshipFields();
        } catch (error) {
          console.error(
            '[PEP] Erro ao chamar verifyPepRelationshipFields:',
            error
          );
        }
      });
    } catch (error) {
      console.error(
        '[PEP] Erro ao configurar subscribe do pep_relationship:',
        error
      );
    }

    // Subscribe nos campos PEP para atualizar o status do formulário quando mudarem
    this.form.controls.pepRelationshipDocument.valueChanges.subscribe(() => {
      setTimeout(() => {
        // Para shareholder, valida apenas os campos obrigatórios (com asterisco)
        const isValid =
          this.userRole === 'shareholder'
            ? this.isShareholderFormValid()
            : this.form.valid;

        this.signupService.setPersonFormStatus('complementaryGeneral', isValid);
      }, 0);
    });

    this.form.controls.pepRelationshipFullName.valueChanges.subscribe(() => {
      setTimeout(() => {
        // Para shareholder, valida apenas os campos obrigatórios (com asterisco)
        const isValid =
          this.userRole === 'shareholder'
            ? this.isShareholderFormValid()
            : this.form.valid;

        this.signupService.setPersonFormStatus('complementaryGeneral', isValid);
      }, 0);
    });

    this.form.controls.pepBond.valueChanges.subscribe(() => {
      setTimeout(() => {
        // Para shareholder, valida apenas os campos obrigatórios (com asterisco)
        const isValid =
          this.userRole === 'shareholder'
            ? this.isShareholderFormValid()
            : this.form.valid;

        this.signupService.setPersonFormStatus('complementaryGeneral', isValid);
      }, 0);
    });

    // Verifica os campos PEP no início caso já estejam preenchidos
    this.verifyPepRelationshipFields();

    // Força uma atualização inicial do status do formulário após todas as inicializações
    // Usa um timeout maior para garantir que todas as validações assíncronas sejam concluídas
    setTimeout(() => {
      // Força a atualização de todos os controles antes de verificar o status
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].updateValueAndValidity({ emitEvent: false });
      });

      const step = this.signupService.getPersonStep();
      const formStatus = this.signupService.getPersonFormStatus(
        'complementaryGeneral'
      );
      const formValid = this.form.valid;

      const invalidControls = Object.keys(this.form.controls)
        .filter((key) => {
          const control = this.form.get(key);
          return control && !control.valid;
        })
        .map((key) => {
          const control = this.form.get(key);
          return {
            key,
            value: control?.value,
            errors: control?.errors,
            valid: control?.valid,
            touched: control?.touched,
            disabled: control?.disabled,
          };
        });

      // Para shareholder, valida apenas os campos obrigatórios (com asterisco)
      const isValid =
        this.userRole === 'shareholder'
          ? this.isShareholderFormValid()
          : this.form.valid;

      this.signupService.setPersonFormStatus('complementaryGeneral', isValid);
    }, 200);

    // if (this.userRole === 'provider') {
    //   this.form.controls.cellphone_number.setValidators([
    //     Validators.required,
    //     Utils.validateWhenNotEmpty(UtilsValidators.celular),
    //   ]);
    //   this.form.controls.telephone_number.setValidators([
    //     Validators.required,
    //     Utils.validateWhenNotEmpty(UtilsValidators.telefone),
    //   ]);
    // }

    // this.form.controls.cellphone_number.valueChanges.subscribe((value) => {
    //   if (this.userRole === 'provider') {
    //     if (value) {
    //       this.form.controls.telephone_number.setValidators([
    //         Utils.validateWhenNotEmpty(UtilsValidators.telefone),
    //       ]);
    //     } else {
    //       this.form.controls.telephone_number.setValidators([
    //         Validators.required,
    //         Utils.validateWhenNotEmpty(UtilsValidators.telefone),
    //       ]);
    //     }
    //   }
    // });

    // this.form.controls.telephone_number.valueChanges.subscribe((value) => {
    //   if (this.userRole === 'provider') {
    //     if (value) {
    //       this.form.controls.cellphone_number.setValidators([
    //         Utils.validateWhenNotEmpty(UtilsValidators.celular),
    //       ]);
    //     } else {
    //       this.form.controls.cellphone_number.setValidators([
    //         Utils.validateWhenNotEmpty(UtilsValidators.celular),
    //         Validators.required,
    //       ]);
    //     }
    //   }
    // });
  }

  verifyPepRelationshipFields() {
    if (this.form.controls.pep_relationship.value) {
      this.form.controls.pepRelationshipDocument.setValidators([
        Validators.required,
        UtilsValidators.cpf,
      ]);
      // Validar o novo campo de exibição
      this.form.controls.pepRelationshipFullName.setValidators([
        Validators.required,
        UtilsValidators.fullName,
      ]);
      // Manter validação no campo antigo para compatibilidade
      this.form.controls.pepRelationshipName.setValidators([
        Validators.required,
        UtilsValidators.fullName,
      ]);
      this.form.controls.pepBond.setValidators([Validators.required]);
    } else {
      this.form.controls.pepRelationshipDocument.setValidators([]);
      this.form.controls.pepRelationshipFullName.setValidators([]);
      this.form.controls.pepRelationshipName.setValidators([]);
      this.form.controls.pepBond.setValidators([]);

      // Limpa os valores dos campos quando o checkbox é desmarcado
      this.form.controls.pepRelationshipDocument.setValue(null);
      this.form.controls.pepRelationshipFullName.setValue(null);
      this.form.controls.pepRelationshipName.setValue(null);
      this.form.controls.pepBond.setValue(null);
    }

    this.form.controls.pepRelationshipDocument.updateValueAndValidity();
    this.form.controls.pepRelationshipFullName.updateValueAndValidity();
    this.form.controls.pepRelationshipName.updateValueAndValidity();
    this.form.controls.pepBond.updateValueAndValidity();

    // Atualiza o status do formulário após alterar os validadores
    // Usa setTimeout para garantir que a validação seja concluída antes de atualizar o status
    setTimeout(() => {
      // Para shareholder, valida apenas os campos obrigatórios (com asterisco)
      const isValid =
        this.userRole === 'shareholder'
          ? this.isShareholderFormValid()
          : this.form.valid;

      this.signupService.setPersonFormStatus('complementaryGeneral', isValid);
    }, 0);
  }

  setMarried() {
    if (
      this.form.controls['marital_status'].value === '2' ||
      this.form.controls['marital_status'].value === '3'
    ) {
      this.form.controls['spouse_name'].setValidators([Validators.required]);
      this.form.controls['spouse_document'].setValidators([
        Validators.required,
        UtilsValidators.cpf,
        (control: FormControl) => {
          const removeSpecialCharacters = control.value
            ? control.value.replace(/[^0-9]/g, '')
            : '';
          const userDocument =
            this.form.controls['document'].value &&
            this.form.controls['document'].value.replace(/[^0-9]/g, '');
          return userDocument === removeSpecialCharacters
            ? { invalid: true }
            : null;
        },
      ]);
      // Para cotista PF, marriage_bond não é obrigatório
      if (this.userRole !== 'shareholder') {
        this.form.controls['marriage_bond'].setValidators([
          Validators.required,
        ]);
      } else {
        this.form.controls['marriage_bond'].setValidators([]);
      }
    } else {
      this.form.controls['spouse_name'].setValidators([]);
      this.form.controls['spouse_document'].setValidators([]);
      this.form.controls['marriage_bond'].setValidators([]);

      // Limpa os valores dos campos quando não é casado/unido
      this.form.controls['spouse_name'].setValue(null);
      this.form.controls['spouse_document'].setValue(null);
      this.form.controls['marriage_bond'].setValue(null);
    }

    this.form.controls.spouse_name.updateValueAndValidity();
    this.form.controls.spouse_document.updateValueAndValidity();
    this.form.controls.marriage_bond.updateValueAndValidity();
  }

  async getDataAndFillForm() {
    // Reset do estado de loading das APIs
    this.loadingAPIs = {
      dataAndFillForm: false,
      funds: false,
      agents: false,
      cities: false,
    };

    // Ativar loading apenas após um pequeno delay para inputs renderizarem primeiro
    // Usar requestAnimationFrame para garantir que o DOM foi atualizado
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (
          !this.loadingAPIs.dataAndFillForm ||
          !this.loadingAPIs.funds ||
          !this.loadingAPIs.agents
        ) {
          this.loadingContent = true;
        }
      }, 200);
    });

    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        this.toast.show(
          'error',
          'Erro',
          'Register UUID não encontrado na rota'
        );
        return;
      }

      const apiRoute = 'api/registration/personal/general/';
      const params = { register_uuid: routeId };

      const { data } = await this.api.get<ApiResponse<Person>>({
        route: apiRoute,
        params: params,
        token: true,
      });

      // Se data é null, significa que ainda não tem dados cadastrados (normal)
      // Não é um erro, apenas não há dados para preencher o formulário
      if (!data || data === null) {
        this.loadingAPIs.dataAndFillForm = true;
        this.checkAndDisableLoading();
        return;
      }

      // Tratar naturalness - pode vir como ID (string "23") ou como nome
      let naturalnessValue: any = null;
      if (data.naturalness) {
        // Se for número ou string numérica, usar diretamente
        if (
          typeof data.naturalness === 'number' ||
          /^\d+$/.test(String(data.naturalness))
        ) {
          naturalnessValue = Number(data.naturalness);
        } else {
          // Se for nome, converter usando getStateIdFromName
          naturalnessValue = this.getStateIdFromName(data.naturalness);
        }
      }

      // Tratar nationality - pode vir como string "Brasileiro" ou como número
      let nationalityValue: string | null = null;
      if (data.nationality) {
        if (typeof data.nationality === 'number') {
          nationalityValue = String(data.nationality);
        } else if (typeof data.nationality === 'string') {
          // Se for string numérica, usar diretamente
          if (/^\d+$/.test(data.nationality)) {
            nationalityValue = data.nationality;
          } else {
            // Se for nome como "Brasileiro", precisa buscar o ID
            // Por enquanto, manter como está e deixar o spread tratar
            nationalityValue = null; // Será tratado pelo spread ou precisa buscar
          }
        }
      }

      // Tratar naturalness_city - pode vir como objeto { id, name } ou como ID
      let naturalnessCityValue: any = null;
      if (data.naturalness_city) {
        if (
          typeof data.naturalness_city === 'object' &&
          data.naturalness_city.id
        ) {
          naturalnessCityValue = data.naturalness_city.id;
        } else if (
          typeof data.naturalness_city === 'number' ||
          typeof data.naturalness_city === 'string'
        ) {
          naturalnessCityValue =
            typeof data.naturalness_city === 'string'
              ? parseInt(data.naturalness_city, 10)
              : data.naturalness_city;
        }
      }

      // Tratar agent - pode vir como array de objetos { id, name } ou como array de IDs
      let agentValue: number[] | null = null;
      const agentData = (data as any).agent;
      if (agentData && Array.isArray(agentData)) {
        agentValue = agentData.map((agent: any) => {
          // Se for objeto com id, usar o id; se for apenas número, usar diretamente
          return typeof agent === 'object' && agent.id ? agent.id : agent;
        });
      }

      let parsedData: Partial<PersonComplementaryGeneralValues> = {
        ...data,
        birth_date: data.birth_date
          ? data.birth_date.split('-').reverse().join('/')
          : null,
        type:
          data.secondary_document?.type?.id != null
            ? String(data.secondary_document.type.id)
            : null,
        rg: data.secondary_document?.number ?? null,
        dispatching_agency: data.secondary_document?.dispatching_agency ?? null,
        uf: data.secondary_document?.uf ?? null,
        expedition_date:
          data.secondary_document?.expedition_date
            ? data.secondary_document.expedition_date
                .split('-')
                .reverse()
                .join('/')
            : null,
        document:
          data.document?.number != null
            ? this.maskPipe.transform(
                data.document.number,
                '000.000.000-00'
              )
            : null,

        gender: data.gender ? String(data.gender) : null,
        marital_status: data.marital_status
          ? String(data.marital_status)
          : null,
        naturalness: naturalnessValue,
        cellphone_number: this.maskPipe.transform(
          data.cellphone?.number ?? null,
          data.cellphone ? '(00) 00000-0000' : null
        ),
        joint_obligation:
          data.assignor?.joint_obligation === 'true',
        sfn: (data as any).sfn ?? false,
        cellphone_ddi: data.cellphone?.code != null ? String(data.cellphone.code) : '55',
        telephone_number:
          data.phone?.number != null
            ? this.maskPipe.transform(
                data.phone.number,
                data.phone.number.length === 10
                  ? '(00) 0000-0000'
                  : '(00) 00000-0000'
              )
            : null,
        telephone_ddi: data.phone?.code != null ? String(data.phone.code) : '55',
        pep_relationship:
          data.pep_relationship &&
          data.pep_relationship.name &&
          data.pep_relationship.name.trim() !== '' &&
          data.pep_relationship.document &&
          data.pep_relationship.document.trim() !== '' &&
          data.pep_relationship.bound &&
          data.pep_relationship.bound.trim() !== ''
            ? true
            : false,
        authorization_bacen_queries: data !== null,
      };

      if (
        data.pep_relationship &&
        data.pep_relationship.name &&
        data.pep_relationship.name.trim() !== '' &&
        data.pep_relationship.document &&
        data.pep_relationship.document.trim() !== '' &&
        data.pep_relationship.bound &&
        data.pep_relationship.bound.trim() !== ''
      ) {
        parsedData.pepRelationshipDocument = data.pep_relationship.document;
        // Usar o novo campo para exibição
        (parsedData as any).pepRelationshipFullName =
          data.pep_relationship.name;
        // Manter o campo antigo para compatibilidade
        parsedData.pepRelationshipName = data.pep_relationship.name;
        parsedData.pepBond = data.pep_relationship.bound;
      }

      if (data.spouse) {
        parsedData.spouse_name = data.spouse?.name ?? null;
        parsedData.spouse_document =
          data.spouse?.document != null
            ? this.maskPipe.transform(data.spouse.document, '000.000.000-00')
            : null;
        // Converte string vazia para null
        const marriageBond = data.spouse.marriage_bond;
        parsedData.marriage_bond =
          marriageBond && String(marriageBond).trim() !== ''
            ? String(marriageBond)
            : null;
      }

      // Usar emitEvent: false para evitar disparar valueChanges durante carregamento inicial
      // Isso previne chamadas duplicadas de getCities
      this.form.patchValue(parsedData, { emitEvent: false });

      // Adicionar nationality, naturalness_city e agent separadamente (não estão no tipo)
      const additionalFields: any = {};
      if (nationalityValue) {
        additionalFields.nationality = nationalityValue;
      } else if (data.nationality) {
        // Se nationalityValue não foi definido mas data.nationality existe,
        // pode ser um formato não tratado anteriormente, usar padrão '23'
        additionalFields.nationality = '23';
      }

      if (naturalnessCityValue) {
        additionalFields.naturalness_city = naturalnessCityValue;
      }

      if (agentValue && agentValue.length > 0) {
        additionalFields.agent = agentValue;
      }

      if (Object.keys(additionalFields).length > 0) {
        this.form.patchValue(additionalFields, { emitEvent: false });
      }

      // Aplicar lógica de nacionalidade estrangeira após carregar dados
      // (necessário porque emitEvent: false não dispara valueChanges)
      const finalNationality =
        nationalityValue ||
        additionalFields.nationality ||
        this.form.controls.nationality.value;
      if (finalNationality && finalNationality !== '23') {
        // Nacionalidade estrangeira - desabilitar e remover obrigatoriedade dos campos de naturalidade
        this.form.controls.naturalness.setValue(null);
        this.form.controls.naturalness_city.setValue(null);
        this.form.controls.naturalness.setValidators([]);
        this.form.controls.naturalness_city.setValidators([]);
        this.isForeign = true;
        this.form.controls.naturalness.updateValueAndValidity({
          emitEvent: false,
        });
        this.form.controls.naturalness_city.updateValueAndValidity({
          emitEvent: false,
        });
      } else if (finalNationality === '23') {
        // Nacionalidade brasileira - habilitar e adicionar obrigatoriedade
        this.form.controls.naturalness.setValidators([Validators.required]);
        this.form.controls.naturalness_city.setValidators([
          Validators.required,
        ]);
        this.isForeign = false;
        this.form.controls.naturalness.updateValueAndValidity({
          emitEvent: false,
        });
        this.form.controls.naturalness_city.updateValueAndValidity({
          emitEvent: false,
        });
      }

      this.data = data;

      this.initialPayload = this.buildPayloadFromForm();

      this.verifyPepRelationshipFields();

      // Após preencher os dados, atualizar validação do marriage_bond baseado no userRole
      // Isso é necessário porque patchValue com emitEvent: false não dispara valueChanges
      this.setMarried();

      // Força atualização do status do formulário após preencher todos os dados
      setTimeout(() => {
        // Força a atualização de todos os controles antes de verificar o status
        Object.keys(this.form.controls).forEach((key) => {
          this.form.controls[key].updateValueAndValidity({ emitEvent: false });
        });

        // Para shareholder, valida apenas os campos obrigatórios (com asterisco)
        const isValid =
          this.userRole === 'shareholder'
            ? this.isShareholderFormValid()
            : this.form.valid;

        this.signupService.setPersonFormStatus('complementaryGeneral', isValid);
      }, 300);

      // Carregar cidades se já existe um estado selecionado
      if (naturalnessValue) {
        await this.getCities(naturalnessValue, true); // true indica carregamento inicial
      } else {
        // Se não tem naturalness, marca cities como carregado (não é necessário)
        this.loadingAPIs.cities = true;
        // Verificar se pode desativar loading após marcar cities
        this.checkAndDisableLoading();
      }

      // Marca getDataAndFillForm como concluído
      this.loadingAPIs.dataAndFillForm = true;
      this.checkAndDisableLoading();
    } catch (error: any) {
      // Em caso de erro, marca como concluído mesmo assim para não travar o loading
      this.loadingAPIs.dataAndFillForm = true;
      const err = error?.error;
      if (err && (err.message || err.data)) {
        const title = err.data || 'Erro';
        const message = err.message || 'Ocorreu um erro ao carregar os dados';
        this.toast.show('error', title, message);
      } else {
        this.toast.show(
          'error',
          'Erro',
          'Ocorreu um erro ao carregar os dados'
        );
      }
      this.checkAndDisableLoading();
    }
  }

  private buildPayloadFromForm(): Record<string, any> {
    const valuesHelper = new GetHelper(this.form.value);
    const birthDate = valuesHelper.get('birth_date');
    const payload: Record<string, any> = {
      document: {
        number: Utils.onlyNumbers(valuesHelper.get('document')),
        type: 'CPF',
      },
      full_name: valuesHelper.get('full_name'),
      email: valuesHelper.get('email'),
      birth_date:
        birthDate && typeof birthDate === 'string'
          ? birthDate.split('/').reverse().join('-')
          : birthDate,
      nationality: valuesHelper.get('nationality'),
      naturalness: valuesHelper.get('naturalness'),
      naturalness_city: valuesHelper.get('naturalness_city'),
      gender: valuesHelper.get('gender'),
      marital_status: valuesHelper.get('marital_status'),
      mother_name: valuesHelper.get('mother_name'),
      father_name: valuesHelper.get('father_name'),
      secondary_document: {
        number: valuesHelper.get('rg'),
        type: valuesHelper.get('type'),
        dispatching_agency: valuesHelper.get('dispatching_agency'),
        uf: valuesHelper.get('uf'),
        expedition_date: (() => {
          const exp = valuesHelper.get('expedition_date');
          return exp && typeof exp === 'string'
            ? exp.split('/').reverse().join('-')
            : exp;
        })(),
      },

      ...(this.userRole !== 'shareholder' && {
        revenues: valuesHelper.get('revenues'),
      }),
      ...(this.userRole !== 'shareholder' && {
        site: valuesHelper.get('site'),
      }),
      ...(this.userRole !== 'shareholder' && {
        institution_relationship: valuesHelper.get(
          'institution_relationship'
        ),
      }),
    };

    if (
      this.form.controls['marital_status'].value === '2' ||
      this.form.controls['marital_status'].value === '3'
    ) {
      payload['spouse'] = {
        document: valuesHelper.get('spouse_document'),
        marriage_bond: valuesHelper.get('marriage_bond'),
        name: valuesHelper.get('spouse_name'),
      };
    }

    if (valuesHelper.get('pep_relationship')) {
      const pepName =
        valuesHelper.get('pepRelationshipFullName') ||
        valuesHelper.get('pepRelationshipName');
      payload['pep_relationship'] = {
        name: pepName,
        document: valuesHelper.get('pepRelationshipDocument'),
        bound: valuesHelper.get('pepBond'),
      };
    } else {
      payload['pep_relationship'] = {
        name: '',
        document: '',
        bound: '',
      };
    }

    // Sempre enviar phone no payload: objeto quando preenchido, null quando vazio (backend limpa ao receber null)
    const telNumber = valuesHelper.get('telephone_number');
    if (telNumber !== '' && telNumber !== null) {
      payload['phone'] = {
        type: 'residential',
        code: Number(valuesHelper.get('telephone_ddi') || 55),
        number: Utils.onlyNumbers(telNumber),
        branch: 0,
      };
    } else {
      payload['phone'] = null;
    }

    // Sempre enviar cellphone no payload: objeto quando preenchido, null quando vazio (backend limpa ao receber null)
    const cellNumber = valuesHelper.get('cellphone_number');
    if (cellNumber !== '' && cellNumber !== null) {
      payload['cellphone'] = {
        type: 'phone',
        code: Number(valuesHelper.get('cellphone_ddi') || 55),
        number: Utils.onlyNumbers(cellNumber),
        branch: 0,
      };
    } else {
      payload['cellphone'] = null;
    }

    // Cedente: mesmo quando `applicable` no auth ainda não reflet assignor.
    if (
      (this.authService.user?.active_register?.register?.role?.applicable ===
        'assignor' &&
        this.userRole !== 'shareholder') ||
      this.userRole === 'assignor'
    ) {
      payload['assignor'] = {
        authorization_bacen_queries: valuesHelper.get(
          'authorization_bacen_queries'
        ),
        relationship_with_other_institutions: valuesHelper.get(
          'relationship_with_other_institutions'
        ),
        relationship_institution: valuesHelper.get(
          'relationship_institution'
        ),
        joint_obligation: String(valuesHelper.get('joint_obligation')),
      };
      payload['sfn'] = !!this.form.get('sfn')?.value;
    }

    if (this.userRole === 'provider') {
      payload['agent'] = valuesHelper.get('agent');
    }

    return payload;
  }

  async sendData() {
    if (this.form.invalid) {
      this.toast.show(
        'error',
        'Erro',
        'Preencha todos os campos obrigatórios'
      );
      return;
    }

    const routeId = this.route.snapshot.paramMap.get('id');
    if (!routeId) {
      this.toast.show(
        'error',
        'Erro',
        'Register UUID não encontrado na rota'
      );
      return;
    }

    const payload = this.buildPayloadFromForm();

    if (payloadsEqual(payload, this.initialPayload ?? undefined)) {
      this.signupService.loading = false;
      this.signupService.setPersonStep('complementaryAddress');
      this.signupService.changePersonStepEvent.emit('address');
      return;
    }

    this.signupService.loading = true;

    try {
      if (this.data) {
        // PUT/PATCH: usa path parameter
        const apiRoute = `api/registration/personal/general/${routeId}/`;

        const { data } = await this.api.put<ApiResponse<Person>>({
          route: apiRoute,
          token: true,
          body: payload,
        });
        this.data = data;
      } else {
        // POST: usa query parameter com register_uuid
        const apiRoute = 'api/registration/personal/general/';
        const params = { register_uuid: routeId };

        const { data } = await this.api.post<ApiResponse<Person>>({
          route: apiRoute,
          params: params,
          token: true,
          body: payload,
        });

        this.data = data;
      }

      this.initialPayload = this.buildPayloadFromForm();
      this.toast.show('info', 'Sucesso', 'Dados salvos com sucesso!');

      this.signupService.changeBasicInfoEvent.emit(payload.full_name);

      this.signupService.setPersonStep('complementaryAddress');
      this.signupService.changePersonStepEvent.emit('address');
    } catch (error) {
      if (error.error) {
        const title = error.error.data || 'Erro';
        const message =
          error.error.message ||
          'Ocorreu um erro ao salvar os dados, tente novamente mais tarde!';
        this.toast.show('error', title, message);
      } else {
        this.toast.show(
          'error',
          'Erro',
          'Ocorreu um erro ao salvar os dados, tente novamente mais tarde!'
        );
      }
      this.signupService.loading = false;
    } finally {
      this.signupService.loading = false;
    }
  }

  async getFunds() {
    try {
      const res = await this.api.get({
        route: 'api/registration/fund',
        token: false,
      });

      const filteredArr = Utils.filterRepeatedValues(res.funds, 'id');

      const funds = filteredArr.map((fund: any) => {
        return {
          label: fund.name,
          value: fund.id,
        };
      });

      this.fundOptions = funds;
      this.loadingAPIs.funds = true;
      this.checkAndDisableLoading();
    } catch (error) {
      // Em caso de erro, marca como concluído mesmo assim para não travar o loading
      this.loadingAPIs.funds = true;
      this.checkAndDisableLoading();
      this.toast.show('error', 'Erro!', 'Erro ao carregar fundos de interesse');
    }
  }

  async getAgents() {
    try {
      const res = await this.api.get({
        route: 'api/registration/simplified-agent/',
      });

      this.agentOptions = res.map((item: any) => {
        return {
          label: item.name,
          value: item.id,
        };
      });

      const newUserDataStorage = this.storageService.getNewUserData();

      if (newUserDataStorage) {
        if (this.userRole === 'provider') {
          this.form.patchValue({
            agent: newUserDataStorage.role,
          });
        }
      }
      this.loadingAPIs.agents = true;
      this.checkAndDisableLoading();
    } catch (error) {
      // Em caso de erro, marca como concluído mesmo assim para não travar o loading
      this.loadingAPIs.agents = true;
      this.checkAndDisableLoading();
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
  }

  async getCities(state: number, isInitialLoad: boolean = false) {
    // Evitar chamadas duplicadas para o mesmo estado
    if (this.citiesLoadingState === state) {
      return;
    }

    this.citiesLoadingState = state;

    try {
      const res = await this.api.get({
        route: `api/registration/city`,
        params: { state },
        token: true,
      });

      this.cities_options = res.map((city: { name: string; id: number }) => ({
        label: city.name,
        value: city.id,
      }));

      // Só define o valor da cidade se não estiver já definido no formulário
      if (
        this.data &&
        this.data.naturalness_city &&
        !this.form.controls.naturalness_city.value
      ) {
        this.form.patchValue({
          naturalness_city: this.data.naturalness_city.id,
        });
      }

      // Se for carregamento inicial, marca como concluído
      if (isInitialLoad) {
        this.loadingAPIs.cities = true;
        this.checkAndDisableLoading();
      }
    } catch (error) {
      // Em caso de erro e for carregamento inicial, marca como concluído mesmo assim
      if (isInitialLoad) {
        this.loadingAPIs.cities = true;
        this.checkAndDisableLoading();
      }
    } finally {
      // Limpa o estado de loading após a requisição terminar
      if (this.citiesLoadingState === state) {
        this.citiesLoadingState = null;
      }
    }
  }

  getStateIdFromName(stateName: string | number): number | null {
    // Se já é um número, retorna como número
    if (typeof stateName === 'number') {
      return stateName;
    }

    // Se é string com menos de 3 caracteres, é um ID numérico como string
    if (
      typeof stateName === 'string' &&
      stateName.length < 3 &&
      stateName !== null &&
      stateName !== ''
    ) {
      const numericId = parseInt(stateName, 10);
      return !isNaN(numericId) ? numericId : null;
    }

    // Se é string e tem mais de 3 caracteres, é nome completo
    if (typeof stateName === 'string' && stateName.length > 3) {
      // Mapeamento de nomes completos para siglas
      const stateNameToSigla: { [key: string]: string } = {
        'São Paulo': 'SP',
        'Rio de Janeiro': 'RJ',
        'Minas Gerais': 'MG',
        Bahia: 'BA',
        Paraná: 'PR',
        'Rio Grande do Sul': 'RS',
        Pernambuco: 'PE',
        Ceará: 'CE',
        Pará: 'PA',
        'Santa Catarina': 'SC',
        Goiás: 'GO',
        Maranhão: 'MA',
        Paraíba: 'PB',
        'Espírito Santo': 'ES',
        Piauí: 'PI',
        Alagoas: 'AL',
        Tocantins: 'TO',
        'Rio Grande do Norte': 'RN',
        Acre: 'AC',
        Amapá: 'AP',
        Amazonas: 'AM',
        'Mato Grosso': 'MT',
        'Mato Grosso do Sul': 'MS',
        Rondônia: 'RO',
        Roraima: 'RR',
        Sergipe: 'SE',
        'Distrito Federal': 'DF',
      };

      const sigla = stateNameToSigla[stateName];
      if (sigla) {
        // Busca o ID correspondente à sigla
        const stateOption = this.signupService.ufs_ids.find(
          (uf) => uf.label === sigla
        );
        return stateOption ? stateOption.value : null;
      }
    }

    // Se é string com 2 caracteres, provavelmente é sigla
    if (typeof stateName === 'string' && stateName.length === 2) {
      const stateOption = this.signupService.ufs_ids.find(
        (uf) => uf.label === stateName.toUpperCase()
      );
      return stateOption ? stateOption.value : null;
    }

    return null;
  }

  /**
   * Método removido - não precisa mais chamar API legacy
   * O backend já cria versão 1 automaticamente quando não existe versão 1
   */
  private async checkAndCreateLegacyVersion(): Promise<void> {
    // Não precisa mais fazer nada - o backend já cria versão 1 automaticamente
    this.legacyVersionChecked = true;
  }
}
